import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiAPI } from '../lib/api';
import { Card, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Skeleton } from '../components/ui/Skeleton';
import { TopBar } from '../components/TopBar';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';

type Tab = 'overview' | 'usage' | 'costs' | 'logs';

const PROVIDER_EMOJIS: Record<string, string> = {
  anthropic: '🤖',
  openai: '🟢',
  gemini: '🔷',
  telegram: '✈️',
  hostinger: '🌐',
};

const PERIOD_OPTIONS = [
  { value: '1', label: '24 год' },
  { value: '7', label: '7 днів' },
  { value: '30', label: '30 днів' },
];

const GROUPBY_OPTIONS = [
  { value: 'provider', label: 'По провайдеру' },
  { value: 'project', label: 'По проєкту' },
  { value: 'day', label: 'По дню' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'Всі статуси' },
  { value: 'ok', label: 'OK' },
  { value: 'error', label: 'Error' },
];

function formatCost(cost: number): string {
  if (cost === 0) return '$0';
  if (cost < 0.001) return `$${cost.toFixed(6)}`;
  if (cost < 0.01) return `$${cost.toFixed(4)}`;
  return `$${cost.toFixed(3)}`;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}мс`;
  return `${(ms / 1000).toFixed(1)}с`;
}

function OverviewTab({ onRefresh }: { onRefresh: () => void }) {
  const { data, isLoading } = useQuery<any[]>({
    queryKey: ['apis-list'],
    queryFn: () => ApiAPI.list(),
  });

  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  async function handleRefresh() {
    setRefreshing(true);
    try {
      const r = await ApiAPI.refreshLogs();
      setToast(`+${r.added} нових подій з логів`);
      setTimeout(() => setToast(null), 3000);
      onRefresh();
    } catch (e: any) {
      setToast(`Помилка: ${e?.message ?? e}`);
      setTimeout(() => setToast(null), 4000);
    } finally {
      setRefreshing(false);
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-24 w-full" />)}
      </div>
    );
  }

  const providers: any[] = data || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-tg-text-secondary">{providers.length} провайдерів</div>
        <Button variant="outline" size="sm" loading={refreshing} onClick={handleRefresh}>
          Оновити з логів
        </Button>
      </div>

      {toast && (
        <div className="rounded-lg px-3 py-2 text-sm" style={{ background: 'var(--tg-accent)', color: 'white' }}>
          {toast}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {providers.map((p) => (
          <Card key={p.slug}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-2xl">{PROVIDER_EMOJIS[p.slug] || '🔑'}</span>
                <div className="min-w-0">
                  <div className="font-semibold text-tg-text truncate">{p.name}</div>
                  <div className="text-xs text-tg-text-secondary font-mono">{p.envKey}</div>
                </div>
              </div>
              <Badge variant={p.hasKey ? 'success' : 'error'}>
                {p.hasKey ? 'Налаштовано' : 'Немає ключа'}
              </Badge>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
              <div>
                <div className="text-tg-text-secondary text-xs">24 год виклики</div>
                <div className="font-semibold text-tg-text">{p.last24h.calls}</div>
              </div>
              <div>
                <div className="text-tg-text-secondary text-xs">24 год вартість</div>
                <div className="font-semibold text-tg-text">{formatCost(p.last24h.costUsd)}</div>
              </div>
            </div>
            {p.lastUsageAt && (
              <div className="mt-2 text-xs text-tg-text-secondary">
                Останній виклик: {new Date(p.lastUsageAt).toLocaleString('uk-UA')}
              </div>
            )}
            {p.notes && (
              <div className="mt-2 text-xs text-tg-text-secondary border-t border-tg-border pt-2">
                {p.notes}
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}

function UsageTab() {
  const [period, setPeriod] = useState('7');
  const [provider, setProvider] = useState('');
  const [project, setProject] = useState('');
  const [offset, setOffset] = useState(0);
  const LIMIT = 50;

  const from = new Date(Date.now() - parseInt(period) * 24 * 60 * 60 * 1000).toISOString();
  const params: Record<string, string> = { from, limit: String(LIMIT), offset: String(offset) };
  if (provider) params.provider = provider;
  if (project) params.project = project;

  const { data, isLoading } = useQuery<{ items: any[]; total: number }>({
    queryKey: ['apis-usage', period, provider, project, offset],
    queryFn: () => ApiAPI.usage(params),
  });

  const { data: providerList } = useQuery<any[]>({
    queryKey: ['apis-list'],
    queryFn: () => ApiAPI.list(),
  });

  const providerOptions = [
    { value: '', label: 'Всі провайдери' },
    ...(providerList || []).map((p) => ({ value: p.slug, label: p.name })),
  ];

  const items = data?.items || [];
  const total = data?.total || 0;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Select options={PERIOD_OPTIONS} value={period} onChange={(e) => { setPeriod(e.target.value); setOffset(0); }} className="w-28" />
        <Select options={providerOptions} value={provider} onChange={(e) => { setProvider(e.target.value); setOffset(0); }} className="w-44" />
        <input
          value={project}
          onChange={(e) => { setProject(e.target.value); setOffset(0); }}
          placeholder="Проєкт..."
          className="rounded-md border border-tg-border px-3 py-2 text-sm text-tg-text outline-none w-36"
          style={{ background: 'var(--tg-bg-secondary)' }}
        />
      </div>

      {isLoading ? (
        <div className="space-y-2">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
      ) : (
        <>
          <div className="text-xs text-tg-text-secondary">Всього: {total} записів</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-tg-border text-tg-text-secondary text-xs">
                  <th className="text-left py-2 pr-3">Час</th>
                  <th className="text-left py-2 pr-3">Провайдер</th>
                  <th className="text-left py-2 pr-3">Проєкт</th>
                  <th className="text-left py-2 pr-3">Модель</th>
                  <th className="text-right py-2 pr-3">Тривалість</th>
                  <th className="text-right py-2">Вартість</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id} className="border-b border-tg-border hover:bg-tg-bg-tertiary/30">
                    <td className="py-1.5 pr-3 text-xs text-tg-text-secondary whitespace-nowrap">
                      {new Date(row.timestamp).toLocaleString('uk-UA')}
                    </td>
                    <td className="py-1.5 pr-3">
                      <span className="text-xs">{PROVIDER_EMOJIS[row.providerSlug] || ''} {row.providerSlug}</span>
                    </td>
                    <td className="py-1.5 pr-3 text-xs text-tg-text-secondary">{row.project || '—'}</td>
                    <td className="py-1.5 pr-3 font-mono text-xs">{row.model || '—'}</td>
                    <td className="py-1.5 pr-3 text-right text-xs">{row.durationMs ? formatDuration(row.durationMs) : '—'}</td>
                    <td className="py-1.5 text-right text-xs">{row.costUsd != null ? formatCost(row.costUsd) : '—'}</td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-tg-text-secondary text-sm">Немає даних</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {total > LIMIT && (
            <div className="flex gap-2 justify-center">
              <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))}>
                Попередня
              </Button>
              <span className="text-xs text-tg-text-secondary self-center">
                {offset + 1}–{Math.min(offset + LIMIT, total)} з {total}
              </span>
              <Button variant="outline" size="sm" disabled={offset + LIMIT >= total} onClick={() => setOffset(offset + LIMIT)}>
                Наступна
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function CostsTab() {
  const [groupBy, setGroupBy] = useState('provider');
  const [period, setPeriod] = useState('30');

  const from = new Date(Date.now() - parseInt(period) * 24 * 60 * 60 * 1000).toISOString();

  const { data, isLoading } = useQuery<any[]>({
    queryKey: ['apis-costs', groupBy, period],
    queryFn: () => ApiAPI.costs({ groupBy, from }),
  });

  const rows = data || [];
  const totalCost = rows.reduce((s, r) => s + r.costUsd, 0);
  const totalCalls = rows.reduce((s, r) => s + r.calls, 0);
  const maxCost = rows.length > 0 ? Math.max(...rows.map((r) => r.costUsd)) : 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Select options={GROUPBY_OPTIONS} value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="w-44" />
        <Select options={PERIOD_OPTIONS} value={period} onChange={(e) => setPeriod(e.target.value)} className="w-28" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Card>
          <div className="text-xs text-tg-text-secondary">Загальна вартість</div>
          <div className="text-2xl font-bold text-tg-text mt-1">{formatCost(totalCost)}</div>
        </Card>
        <Card>
          <div className="text-xs text-tg-text-secondary">Всього викликів</div>
          <div className="text-2xl font-bold text-tg-text mt-1">{totalCalls.toLocaleString()}</div>
        </Card>
      </div>

      {isLoading ? (
        <div className="space-y-2">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : rows.length === 0 ? (
        <p className="text-sm text-tg-text-secondary">Немає даних</p>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => {
            const barWidth = maxCost > 0 ? (row.costUsd / maxCost) * 100 : 0;
            return (
              <div key={row.key} className="space-y-1">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-tg-text font-medium truncate max-w-xs">
                    {PROVIDER_EMOJIS[row.key] ? `${PROVIDER_EMOJIS[row.key]} ` : ''}{row.key}
                  </span>
                  <span className="text-tg-text-secondary text-xs ml-2 shrink-0">
                    {row.calls} викл. · {formatCost(row.costUsd)}
                  </span>
                </div>
                <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--tg-bg)' }}>
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${barWidth}%`, background: 'var(--tg-accent)' }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function LogsTab() {
  const [provider, setProvider] = useState('');
  const [status, setStatus] = useState('');
  const [offset, setOffset] = useState(0);
  const LIMIT = 50;

  const { data: providerList } = useQuery<any[]>({
    queryKey: ['apis-list'],
    queryFn: () => ApiAPI.list(),
  });

  const providerOptions = [
    { value: '', label: 'Всі провайдери' },
    ...(providerList || []).map((p) => ({ value: p.slug, label: p.name })),
  ];

  const params: Record<string, string> = { limit: String(LIMIT), offset: String(offset) };
  if (provider) params.provider = provider;
  if (status) params.status = status;

  const { data, isLoading } = useQuery<{ items: any[]; total: number }>({
    queryKey: ['apis-logs', provider, status, offset],
    queryFn: () => ApiAPI.usage(params),
  });

  const items = data?.items || [];
  const total = data?.total || 0;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Select options={providerOptions} value={provider} onChange={(e) => { setProvider(e.target.value); setOffset(0); }} className="w-44" />
        <Select options={STATUS_OPTIONS} value={status} onChange={(e) => { setStatus(e.target.value); setOffset(0); }} className="w-32" />
      </div>

      {isLoading ? (
        <div className="space-y-2">{[1,2,3,4,5].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
      ) : (
        <>
          <div className="text-xs text-tg-text-secondary">Всього: {total} записів</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-tg-border text-tg-text-secondary text-xs">
                  <th className="text-left py-2 pr-3">Час</th>
                  <th className="text-left py-2 pr-3">Провайдер</th>
                  <th className="text-left py-2 pr-3">Проєкт</th>
                  <th className="text-left py-2 pr-3">Модель</th>
                  <th className="text-right py-2 pr-3">Тривалість</th>
                  <th className="text-right py-2 pr-3">Вартість</th>
                  <th className="text-center py-2">Статус</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id} className="border-b border-tg-border hover:bg-tg-bg-tertiary/30">
                    <td className="py-1.5 pr-3 text-xs text-tg-text-secondary whitespace-nowrap">
                      {new Date(row.timestamp).toLocaleString('uk-UA')}
                    </td>
                    <td className="py-1.5 pr-3 text-xs">{PROVIDER_EMOJIS[row.providerSlug] || ''} {row.providerSlug}</td>
                    <td className="py-1.5 pr-3 text-xs text-tg-text-secondary">{row.project || '—'}</td>
                    <td className="py-1.5 pr-3 font-mono text-xs">{row.model || '—'}</td>
                    <td className="py-1.5 pr-3 text-right text-xs">{row.durationMs ? formatDuration(row.durationMs) : '—'}</td>
                    <td className="py-1.5 pr-3 text-right text-xs">{row.costUsd != null ? formatCost(row.costUsd) : '—'}</td>
                    <td className="py-1.5 text-center">
                      <Badge variant={row.status === 'ok' ? 'success' : 'error'}>{row.status}</Badge>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-6 text-center text-tg-text-secondary text-sm">Немає даних</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {total > LIMIT && (
            <div className="flex gap-2 justify-center">
              <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))}>
                Попередня
              </Button>
              <span className="text-xs text-tg-text-secondary self-center">
                {offset + 1}–{Math.min(offset + LIMIT, total)} з {total}
              </span>
              <Button variant="outline" size="sm" disabled={offset + LIMIT >= total} onClick={() => setOffset(offset + LIMIT)}>
                Наступна
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function ApisPage() {
  const [tab, setTab] = useState<Tab>('overview');
  const qc = useQueryClient();

  function refreshAll() {
    qc.invalidateQueries({ queryKey: ['apis-list'] });
    qc.invalidateQueries({ queryKey: ['apis-usage'] });
    qc.invalidateQueries({ queryKey: ['apis-costs'] });
    qc.invalidateQueries({ queryKey: ['apis-logs'] });
  }

  const tabs: [Tab, string][] = [
    ['overview', 'Огляд'],
    ['usage', 'Використання'],
    ['costs', 'Витрати'],
    ['logs', 'Логи'],
  ];

  return (
    <div>
      <TopBar title="API" />
      <div className="px-4 md:px-6 py-4 space-y-4">
        <div className="flex gap-1 border-b border-tg-border overflow-x-auto">
          {tabs.map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className="px-3 py-2 text-sm whitespace-nowrap transition-colors border-b-2"
              style={{
                borderColor: tab === key ? 'var(--tg-accent)' : 'transparent',
                color: tab === key ? 'var(--tg-accent)' : 'var(--tg-text-secondary)',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === 'overview' && <OverviewTab onRefresh={refreshAll} />}
        {tab === 'usage' && <UsageTab />}
        {tab === 'costs' && <CostsTab />}
        {tab === 'logs' && <LogsTab />}
      </div>
    </div>
  );
}
